import React, { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { useStore } from "../context/StoreContext";
import { useSeo } from "../hooks/useSeo";

const TITLES = {
  men: "Men", women: "Women", men40: "Men Over 40", women40: "Women Over 40",
  bundles: "Bundles", sale: "Sale",
};
const BLURB = {
  all: "Every clean, high-strength supplement we make — nothing added, nothing hidden.",
  men: "Strength, drive and daily performance, cleanly done.",
  women: "Beauty, balance and natural vitality from the inside out.",
  men40: "Testosterone, joints and longevity support built for life after 40.",
  women40: "Hormones, bones and glow — formulated for women over 40.",
  bundles: "Curated stacks that work together and save you money.",
  sale: "Genuine reductions on clean supplements. No pretend sales.",
};

const Shop = () => {
  const { category } = useParams();
  const key = category || "all";
  const [sort, setSort] = useState("featured");
  const { products: PRODUCTS, getByCategory, sections } = useStore();
  const CATEGORIES = sections;
  const section = CATEGORIES.find((c) => c.key === key);
  useSeo({
    title: section ? `${section.label} Supplements UK` : "Shop All Clean Supplements",
    description: section
      ? `${section.label} supplements made in the UK – ${section.blurb}. Clean, high-strength formulas with no fillers or magnesium stearate.`
      : "Shop every British Supplements product: clean, high-strength, UK-made supplements with no fillers. Free UK shipping over £40.",
    image: section?.image,
  });

  const base = useMemo(() => (key === "all" ? PRODUCTS : getByCategory(key)), [key, PRODUCTS]);
  const products = useMemo(() => {
    const list = [...base];
    if (sort === "low") list.sort((a, b) => a.price - b.price);
    if (sort === "high") list.sort((a, b) => b.price - a.price);
    if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [base, sort]);

  return (
    <div>
      {/* Banner */}
      <div className="relative overflow-hidden bg-[#1B4332]">
        {key !== "all" && CATEGORIES.find((c) => c.key === key) && (
          <img src={CATEGORIES.find((c) => c.key === key).image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d281d] to-[#1B4332]/70" />
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <div className="mb-2 flex items-center gap-2 text-xs text-[#95D5B2]">
            <Link to="/" className="hover:text-white">Home</Link><span>/</span>
            <span className="text-white">{key === "all" ? "All Products" : TITLES[key]}</span>
          </div>
          <h1 className="font-display text-4xl font-bold uppercase text-white sm:text-5xl">
            {key === "all" ? "All Products" : TITLES[key]}
          </h1>
          <p className="mt-3 max-w-xl text-[#cfe0d6]">{BLURB[key]}</p>
        </div>
      </div>

      {/* Category chips */}
      <div className="border-b border-[#e6f0ea] bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-4 py-4 sm:px-6">
          <Link to="/shop" className={`rounded-full px-4 py-2 font-display text-xs font-semibold uppercase tracking-wider transition ${key === "all" ? "bg-[#1B4332] text-white" : "bg-[#f0f7f2] text-[#2D6A4F] hover:bg-[#dcefe3]"}`}>All</Link>
          {CATEGORIES.map((c) => (
            <Link key={c.key} to={`/shop/${c.key}`} className={`rounded-full px-4 py-2 font-display text-xs font-semibold uppercase tracking-wider transition ${key === c.key ? "bg-[#1B4332] text-white" : "bg-[#f0f7f2] text-[#2D6A4F] hover:bg-[#dcefe3]"}`}>
              {c.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex items-center justify-between">
          <p className="text-sm text-[#6b7d72]">{products.length} product{products.length !== 1 ? "s" : ""}</p>
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={16} className="text-[#2D6A4F]" />
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-full border border-[#dceadf] bg-white px-4 py-2 font-display text-xs font-semibold uppercase tracking-wider text-[#2D6A4F] outline-none focus:border-[#52B788]">
              <option value="featured">Featured</option>
              <option value="low">Price: Low to High</option>
              <option value="high">Price: High to Low</option>
              <option value="rating">Top Rated</option>
            </select>
          </div>
        </div>

        {products.length === 0 ? (
          <div className="py-20 text-center text-[#6b7d72]">No products in this section yet.</div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Shop;
